import { Component } from "@theme/component";
import { DialogComponent, DialogCloseEvent } from "@theme/dialog";

/**
 * A front room booking popup component that extends the DialogComponent
 * to show a booking popup after a specified delay.
 *
 * @typedef {Object} FrontRoomBookingPopUpRefs
 * @property {HTMLDialogElement} dialog - The dialog element for the popup
 * @property {HTMLButtonElement} bookingButton - The booking button inside the popup
 */

class FrontRoomBookingPopUp extends DialogComponent {
  #abortController = new AbortController();

  requiredRefs = ["dialog", "bookingButton"];

  /** @type {FrontRoomBookingPopUpRefs} */
  refs;

  /** @type {number | null} */
  #popupTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.refs.bookingButton.addEventListener("mousedown", this.closeDialog);
    this.startTimeoutForPopup();
  }

  startTimeoutForPopup() {
    const popupDelay =
      Number(this.refs.dialog.getAttribute("data-popup-delay")) || 0;

    if (popupDelay > 0) {
      this.#popupTimeout = setTimeout(() => {
        this.showDialog();
        this.refs.bookingButton.focus();
      }, popupDelay * 1000);
    } else {
      this.showDialog();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#popupTimeout) {
      clearTimeout(this.#popupTimeout);
      this.#popupTimeout = null;
    }

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );

    this.refs.bookingButton.removeEventListener("mousedown", this.closeDialog);
  }

  #handleDialogClose = () => {
    if (this.#popupTimeout) {
      clearTimeout(this.#popupTimeout);
      this.#popupTimeout = null;
    }
  };
}

if (!customElements.get("front-room-booking-pop-up")) {
  customElements.define("front-room-booking-pop-up", FrontRoomBookingPopUp);
}
